import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { BibleBook, BiblePassage } from '../../shared/api/types';
import { Button, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink } from './BiblePage';

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, '');

/** «LAMENTACIONES 3:21-26», «1 Juan 1:9», «Heb 8:7-8», «Salmos 23». */
const CITE = /(?<![\p{L}\d])((?:[1-3]\s*)?\p{L}{3,})\.?\s+(\d{1,3})(?:\s*:\s*(\d{1,3})(?:\s*[-–]\s*(\d{1,3}))?)?(?![\p{L}\d])/gu;

/** El libro por su nombre completo o un inicio que solo le corresponde a él (como el lector de KEORA). */
export function matchBook(books: readonly BibleBook[], text: string): BibleBook | null {
  const key = fold(text);
  const exact = books.find((b) => fold(b.name) === key);
  if (exact) return exact;
  const byPrefix = books.filter((b) => fold(b.name).startsWith(key));
  return byPrefix.length === 1 ? byPrefix[0]! : null;
}

let booksCache: Promise<BibleBook[]> | null = null;
/** Los libros se piden una vez (un texto con muchos subrayados usa varios ScriptureText). */
export function bibleBooks(): Promise<BibleBook[]> {
  booksCache ??= api.get<{ items: BibleBook[] }>('/bible/books').then((r) => r.items).catch((e) => {
    booksCache = null;
    throw e;
  });
  return booksCache;
}

/** Pruebas: cada una con sus propios libros simulados. */
export function resetBibleBooks() {
  booksCache = null;
}

type Part = string | { text: string; ref: string };

/** Separa el texto en partes y citas válidas (libro que existe y capítulo dentro del libro). */
export function findCitations(text: string, books: readonly BibleBook[]): Part[] {
  const parts: Part[] = [];
  let last = 0;
  const re = new RegExp(CITE.source, 'gu');
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const book = matchBook(books, m[1]!);
    const chapter = Number(m[2]);
    if (!book || chapter < 1 || chapter > book.chapters) {
      // «luego 1 Juan 1:9»: lo que no es cita no se come el inicio de la siguiente.
      re.lastIndex = m.index + 1;
      continue;
    }
    const ref = `${book.name} ${chapter}${m[3] ? `:${m[3]}${m[4] ? `-${m[4]}` : ''}` : ''}`;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ text: m[0], ref });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Citas de la Biblioteca que hay en el texto: «⟪Kauflin, Nuestra adoración importa, p. 45⟫». */
export type LibraryCitation = { id: string; label: string; libraryItemId: string; title: string; author: string | null; page: number; fragment: string };
export const CitationContext = createContext<{ churchId: string; byLabel: Map<string, LibraryCitation> } | null>(null);
const LIB_CITE = /⟪([^⟫\n]{1,200})⟫/g;

/**
 * Texto (p. ej. las notas de una clase) con las citas bíblicas convertidas en enlaces: al tocarlas
 * se abre el pasaje en una ventana. No cambia el texto guardado.
 */
export function ScriptureText({ text, className = 'pre-line' }: { text: string; className?: string }) {
  const books = useAsync(() => bibleBooks(), []);
  const [open, setOpen] = useState<string | null>(null);
  const [cite, setCite] = useState<LibraryCitation | null>(null);
  const library = useContext(CitationContext);
  // Primero las citas de la Biblioteca («⟪…⟫», se ven entre paréntesis con el mismo largo); luego las bíblicas.
  const chunks: (string | LibraryCitation | { missing: string })[] = [];
  let last = 0;
  for (const m of text.matchAll(LIB_CITE)) {
    if (m.index! > last) chunks.push(text.slice(last, m.index));
    chunks.push(library?.byLabel.get(m[1]!) ?? { missing: m[0] });
    last = m.index! + m[0].length;
  }
  if (last < text.length) chunks.push(text.slice(last));
  type Piece = Part | LibraryCitation | { missing: string };
  const parts = chunks.flatMap<Piece>((c) => (typeof c === 'string' ? (books.status === 'ready' ? findCitations(c, books.data) : [c]) : [c]));
  return (
    <>
      <span className={className}>
        {parts.map((p, i) =>
          typeof p === 'object' && 'fragment' in p ? (
            <button key={i} type="button" className="library-link" onClick={() => setCite(p)}>
              ({p.label})
            </button>
          ) : typeof p === 'object' && 'missing' in p ? (
            `(${p.missing.slice(1, -1)})`
          ) : typeof p === 'string' ? (
            p
          ) : (
            <button key={i} type="button" className="scripture-link" onClick={() => setOpen(p.ref)}>
              {p.text}
            </button>
          ),
        )}
      </span>
      {open ? <PassageModal reference={open} onClose={() => setOpen(null)} /> : null}
      {cite && library ? <CitationModal c={cite} churchId={library.churchId} onClose={() => setCite(null)} /> : null}
    </>
  );
}

/** Ventana con el fragmento citado, el libro, el autor y la página. */
function CitationModal({ c, churchId, onClose }: { c: LibraryCitation; churchId: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="scripture-modal" onClick={onClose}>
      <div className="scripture-modal__box card" role="dialog" aria-modal="true" aria-label={c.title} onClick={(e) => e.stopPropagation()}>
        <header className="card__header">
          <h2 className="card__title">{c.title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Cerrar">
            ✕
          </Button>
        </header>
        <p className="small muted">
          {c.author ? `${c.author} · ` : ''}página {c.page}
        </p>
        <blockquote className="library-quote">{c.fragment}</blockquote>
        <div className="actions">
          <a className="btn btn--secondary" href={`/api/media/churches/${churchId}/library/${c.libraryItemId}/file?ver=1#page=${c.page}`} target="_blank" rel="noopener noreferrer">
            Abrir el libro en esta página
          </a>
        </div>
      </div>
    </div>
  );
}

/** Ventana con el pasaje y «Abrir en la Biblia». */
export function PassageModal({ reference, onClose }: { reference: string; onClose: () => void }) {
  // La traducción de siempre de KEORA (la sigla que traiga la nota, p. ej. RVR1960, no se usa).
  const passage = useAsync((signal) => api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(reference)}`, signal), [reference]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const p = passage.status === 'ready' ? passage.data : null;
  return (
    <div className="scripture-modal" onClick={onClose}>
      <div className="scripture-modal__box card" role="dialog" aria-modal="true" aria-label={p?.label ?? reference} onClick={(e) => e.stopPropagation()}>
        <header className="card__header">
          <h2 className="card__title">{p?.label ?? reference}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Cerrar">
            ✕
          </Button>
        </header>
        {passage.status === 'loading' ? <Loading /> : null}
        {passage.status === 'error' ? <ErrorState error={passage.error} /> : null}
        {p ? (
          <div className="scripture-modal__text bible__text">
            {p.verses.length === 0 ? <p className="muted">Aún no hay texto para esta traducción.</p> : null}
            {p.verses.map((v) => (
              <span key={v.verse}>
                <sup>{v.verse}</sup> {v.text}{' '}
              </span>
            ))}
          </div>
        ) : null}
        <div className="actions">
          <Link className="btn btn--secondary" to={bibleLink(reference)} onClick={onClose}>
            Abrir en la Biblia
          </Link>
        </div>
      </div>
    </div>
  );
}
